import styled from "styled-components"
import moment from "moment"
import LockOutlined from "@mui/icons-material/LockOutlined"
import { ProofOfOwnership } from "~shapes/proofOfOwnership"
import { PROOF_OF_OWNERSHIP_STATUS } from "~constants/proofOfOwnership"
import useOperatorLabel from "~hooks/useOperatorLabel"
import colors from "~constants/colors"
import { rgba } from "polished"

interface Props {
  item: ProofOfOwnership
  selectedItem: ProofOfOwnership
  onClick: (v: ProofOfOwnership) => void
  // Only the global Validating list shows who's validating; My validations is all
  // the caller's own, so we skip the chip (and its lookup) there.
  showAssignee?: boolean
}

const stateColors: Record<string, string> = {
  [PROOF_OF_OWNERSHIP_STATUS.CREATED]: colors.blue2,
  [PROOF_OF_OWNERSHIP_STATUS.VALIDATING]: "#F5A623",
  [PROOF_OF_OWNERSHIP_STATUS.VALIDATED]: colors.green,
  [PROOF_OF_OWNERSHIP_STATUS.INVALIDATED]: colors.red,
  [PROOF_OF_OWNERSHIP_STATUS.DISCARDED]: colors.gray4,
  [PROOF_OF_OWNERSHIP_STATUS.UPDATING]: colors.sky,
  [PROOF_OF_OWNERSHIP_STATUS.UNDETERMINED]: colors.gray6,
}

const ProofOfOwnershipListItem = ({
  item,
  onClick,
  selectedItem,
  showAssignee,
}: Props) => {
  const isSelected = item.id === selectedItem.id
  const shortId = item.id.slice(-8)
  const date = item.created_at
    ? moment(item.created_at).format("MMM D, YYYY")
    : ""
  const dotColor = stateColors[item.state] || colors.gray4
  // Who is validating this one (email / SYSTEM name); null when it's yours or
  // unassigned. Only resolved in the global Validating list ("who is validating
  // what") — passing undefined in My validations skips the chip and the lookup.
  const assigneeLabel = useOperatorLabel(
    showAssignee ? item.assignee : undefined,
  )

  return (
    <ItemRow $isSelected={isSelected} onClick={() => onClick(item)}>
      <StateDot $color={dotColor} />
      <ItemContent>
        <ItemPrimary>
          {item.broker || "No broker"}
          <ItemId>#{shortId}</ItemId>
        </ItemPrimary>
        <ItemSecondary>
          {date}
          {item.name && ` · ${item.name}`}
        </ItemSecondary>
      </ItemContent>
      {assigneeLabel && (
        <AssigneeChip title={`Being validated by ${assigneeLabel}`}>
          <LockOutlined style={{ fontSize: 12 }} />
          {assigneeLabel}
        </AssigneeChip>
      )}
    </ItemRow>
  )
}

const ItemRow = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  cursor: pointer;
  border-radius: 6px;
  background: ${props =>
    props.$isSelected ? rgba(colors.blue1, 0.08) : "transparent"};
  border-left: 3px solid
    ${props => (props.$isSelected ? colors.blue1 : "transparent")};
  transition: all 0.12s ease;

  &:hover {
    background: ${props =>
      props.$isSelected ? rgba(colors.blue1, 0.08) : colors.gray1};
  }

  & + & {
    margin-top: 2px;
  }
`

const StateDot = styled.span<{ $color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$color};
  flex-shrink: 0;
`

const ItemContent = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
`

const ItemPrimary = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.gray3};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemId = styled.span`
  font-size: 11px;
  font-weight: 400;
  color: ${colors.gray6};
  font-family: monospace;
`

const ItemSecondary = styled.div`
  font-size: 11px;
  color: ${colors.gray6};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const AssigneeChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
  max-width: 150px;
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.gray6};
  background: ${rgba(colors.gray6, 0.12)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default ProofOfOwnershipListItem
